import isEqual from 'lodash/isEqual';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { setStateAction } from 'editor/src/store/design/slice';
import { DesignData, DesignState, MediaText, TextStyle } from 'editor/src/store/design/types';
import { isInReflowRow } from 'editor/src/store/design/util/sharedTextFields';
import {
  buildTextStyleElementUpdate,
  getTextStyle,
  getTextStyleMembers,
  isTextStyleProtected,
  measureTextStyleElement,
} from 'editor/src/store/design/util/textStyles';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { popPreviousStateAction, replaceStatesAction } from 'editor/src/store/editorModules/undoRedo/slice';
import { SavedState } from 'editor/src/store/editorModules/undoRedo/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/**
 * GAPI-61895: an "Edit style" form session. Edits made while the form is open apply live to
 * every text following the style (updateTextStyleOperation with `saveUndo: false`); the one
 * undo step is the snapshot saved when the form opened.
 */
export interface TextStyleEditSession {
  styleId: string;
  /** The style as it was when the form opened. */
  original: TextStyle;
  /** The design as it was when the form opened. */
  design: DesignState;
  /** The undo step this session pushed, if it pushed one (undo may be off, or deduplicated). */
  undoStep: SavedState | undefined;
  /** The last undo step when the form opened (undoStep, or the step it repeated); undefined when undo is off. */
  baseStep: SavedState | undefined;
}

const topUndoStep = (state: RootState) => {
  const { previous } = state.editorModules.undoRedo;
  return previous[previous.length - 1] as SavedState | undefined;
};

/** Opens the session: saves the undo step that will revert the whole edit. */
export const beginTextStyleEditOperation =
  (styleId: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): TextStyleEditSession | undefined => {
    const original = getTextStyle(getState().design.designData, styleId);
    if (!original) {
      return undefined;
    }
    const stepsBefore = getState().editorModules.undoRedo.previous.length;
    dispatch(saveUndoRedoStateOperation('Text style edited'));
    const state = getState();
    return {
      styleId,
      original,
      design: state.design,
      undoStep: state.editorModules.undoRedo.previous.length > stepsBefore ? topUndoStep(state) : undefined,
      baseStep: state.editorModules.undoRedo.saveSteps ? topUndoStep(state) : undefined,
    };
  };

/**
 * Done: keeps the edits. The step saved on open is the session's one undo step; when nothing
 * changed it is dropped again, so opening and closing the form leaves no empty step behind.
 */
export const commitTextStyleEditOperation =
  (session: TextStyleEditSession) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    if (
      session.undoStep &&
      topUndoStep(state) === session.undoStep &&
      state.design.designData === session.design.designData
    ) {
      dispatch(popPreviousStateAction());
    }
  };

/** Every key either side has, `path` left out (see revertMember). */
const keysOf = (...objects: object[]) =>
  [...new Set(objects.flatMap((object) => Object.keys(object)))].filter((key) => key !== 'path');

/**
 * Takes one text back from what the session wrote to it, key by key (three-way, with `extra`
 * merged per key too): a value still as the session left it goes back to its value when the
 * form opened; a value changed after the session (moved, retyped, recoloured...) is kept.
 * What the session left is recomputed from the opening text and the style it was given
 * (buildTextStyleElementUpdate is deterministic), so a later change to the very value the
 * session set, back to that same value, cannot be told apart and is reverted too.
 */
function revertMember(target: MediaText, base: MediaText, style: TextStyle): MediaText {
  const sessionEnd = { ...base, ...buildTextStyleElementUpdate(base, style) } as MediaText;
  let reverted = false;
  let changedLater = false;
  const merge = <T extends object>(t: T, s: T, b: T, skip: string[] = []) => {
    const result = { ...t } as Record<string, unknown>;
    const [tr, sr, br] = [t, s, b] as Record<string, unknown>[];
    keysOf(t, s, b)
      .filter((key) => !skip.includes(key))
      .forEach((key) => {
        if (!isEqual(tr[key], sr[key])) {
          changedLater = true;
        } else if (!isEqual(sr[key], br[key])) {
          reverted = true;
          if (br[key] === undefined) {
            delete result[key];
          } else {
            result[key] = br[key];
          }
        }
      });
    return result as T;
  };
  const merged = merge(target, sessionEnd, base, ['extra']);
  merged.extra = merge(target.extra, sessionEnd.extra, base.extra);
  if (!reverted) {
    return target;
  }
  if (!changedLater) {
    // Untouched since the session: exactly as it was, server-rendered path included.
    return base;
  }
  // Changed since: the old path may not match the text any more (it is re-rendered when
  // missing), and the box is re-measured for the values that went back.
  return { ...merged, path: target.path, ...measureTextStyleElement(merged) };
}

/**
 * The design with the session's edit taken out: the style back to its values when the form
 * opened, and each text the session restyled reverted by revertMember. Returns `designData`
 * itself when there is nothing to revert (the style is as it was, or gone).
 */
function revertTextStyleEdit(
  designData: DesignData | undefined,
  session: TextStyleEditSession,
  restyledUuids: Set<number>,
  revertedSpreads?: Set<number>,
): DesignData | undefined {
  const { original } = session;
  const style = getTextStyle(designData, original.id);
  if (!designData || !style || isEqual(style, original)) {
    return designData;
  }
  const baseMembers = new Map(
    getTextStyleMembers(session.design.designData, original.id)
      .filter(({ element }) => restyledUuids.has(element.uuid))
      .map(({ element }) => [element.uuid, element]),
  );
  const spreads = designData.spreads.map((spread, spreadIndex) => {
    let spreadChanged = false;
    const pages = spread.pages.map((page) => {
      let pageChanged = false;
      const media = page.groups.media?.map((element) => {
        const base = baseMembers.get(element.uuid);
        if (element.type !== 'text' || element.extra.textStyleId !== original.id || !base) {
          return element;
        }
        const reverted = revertMember(element, base, style);
        if (reverted !== element) {
          pageChanged = true;
          revertedSpreads?.add(spreadIndex);
        }
        return reverted;
      });
      if (!pageChanged) {
        return page;
      }
      spreadChanged = true;
      return { ...page, groups: { ...page.groups, media } };
    });
    return spreadChanged ? { ...spread, pages } : spread;
  });
  return {
    ...designData,
    textStyles: designData.textStyles?.map((item) => (item.id === original.id ? original : item)),
    spreads,
  };
}

/**
 * Cancel: puts the style and its texts back as they were when the form opened.
 *
 * While the session's step is still the last one nothing else undoable happened since, so the
 * design is restored as a whole (members keep the own modifications they had) and the step
 * dropped.
 *
 * Otherwise other work happened while the form was open and must survive: the style gets its
 * opening values back, and each text it restyled only the values the session wrote and nothing
 * changed since (revertMember). The steps saved after the session's step are rewritten the same
 * way and the session's own step dropped, so undo does not bring the cancelled values back.
 *
 * Either way the redo steps are rewritten too. When the step saved on open is no longer in the
 * history (undone while the form was open, or pushed out by the step limit) the history cannot
 * be told apart from what came before the session, so only the design is reverted.
 */
export const cancelTextStyleEditOperation =
  (session: TextStyleEditSession) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    // The texts the session restyled: protected ones were left alone (applyTextStyleToMembersOperation).
    const restyledUuids = new Set(
      getTextStyleMembers(session.design.designData, session.styleId)
        .filter(({ element }) => !isTextStyleProtected(state, element))
        .map(({ element }) => element.uuid),
    );
    const rebase = (step: SavedState): SavedState => {
      const stepData = revertTextStyleEdit(step.design.designData, session, restyledUuids);
      return stepData === step.design.designData ? step : { ...step, design: { ...step.design, designData: stepData } };
    };
    const { previous, next } = state.editorModules.undoRedo;

    if (session.undoStep && topUndoStep(state) === session.undoStep) {
      batch(() => {
        if (state.design.designData !== session.design.designData) {
          dispatch(setStateAction(session.design));
        }
        // Drops the session's step. Redo steps (work undone while the form was open) carry the
        // session's values too, so they are rewritten.
        dispatch(replaceStatesAction({ previous: previous.slice(0, -1), next: next.map(rebase) }));
      });
      return Promise.resolve();
    }

    const revertedSpreads = new Set<number>();
    const designData = revertTextStyleEdit(state.design.designData, session, restyledUuids, revertedSpreads);
    // Only when the base step is still in the history are all the other steps known to be newer
    // than the session: an older step can hold an older version of the style, not to be touched.
    const baseIndex = session.baseStep ? previous.indexOf(session.baseStep) : -1;

    batch(() => {
      if (designData !== state.design.designData) {
        dispatch(setStateAction({ ...state.design, designData }));
        // A row packs from every member's width (as in applyTextStyleToMembersOperation).
        revertedSpreads.forEach((spreadIndex) => {
          if (
            getTextStyleMembers(designData, session.styleId).some(
              ({ element, address }) =>
                address.spreadIndex === spreadIndex && isInReflowRow(designData, spreadIndex, element.uuid),
            )
          ) {
            dispatch(updateHiddenElementsOperation(spreadIndex));
          }
        });
      }
      if (baseIndex >= 0) {
        // The session's own step is dropped: with the later steps rewritten it only repeats the next one.
        const ownStep = session.undoStep === session.baseStep;
        dispatch(
          replaceStatesAction({
            previous: [
              ...previous.slice(0, ownStep ? baseIndex : baseIndex + 1),
              ...previous.slice(baseIndex + 1).map(rebase),
            ],
            next: next.map(rebase),
          }),
        );
      }
    });
    return Promise.resolve();
  };
